import { useState } from 'react';
import { Loader2, Smartphone } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { accessControlAction } from '@/lib/api';

interface SmsVerificationProps {
  deviceId: string;
  onVerified: (token: string) => void;
  onCancel: () => void;
}

type Status = 'idle' | 'sending' | 'code_sent' | 'verifying' | 'failed';

export function SmsVerification({ deviceId, onVerified, onCancel }: SmsVerificationProps) {
  const [status, setStatus] = useState<Status>('idle');
  const [orderRef, setOrderRef] = useState<string | null>(null);
  const [code, setCode] = useState('');
  const [error, setError] = useState<string | null>(null);

  const sendCode = async () => {
    setStatus('sending');
    setError(null);

    const result = await accessControlAction<{
      order_ref: string;
      method: string;
      message: string;
    }>('initiate-verification', {
      device_id: deviceId,
      method: 'mobile',
    });

    if (result.error || !result.data) {
      setError(result.error || 'Kunde inte skicka SMS');
      setStatus('failed');
      return;
    }

    setOrderRef(result.data.order_ref);
    setStatus('code_sent');
  };

  const verifyCode = async () => {
    if (!orderRef || code.length !== 6) return;
    setStatus('verifying');
    setError(null);

    const result = await accessControlAction<{
      status: string;
      verification_token?: string;
    }>('collect-verification', {
      order_ref: orderRef,
      method: 'mobile',
    });

    if (result.error) {
      setError(result.error);
      setStatus('code_sent');
      return;
    }

    if (result.data?.status === 'complete' && result.data.verification_token) {
      onVerified(result.data.verification_token);
    } else {
      setError('Felaktig eller utgången kod');
      setStatus('code_sent');
    }
  };

  return (
    <div className="space-y-4">
      <h3 className="text-lg font-semibold">SMS-verifiering</h3>
      <p className="text-sm text-muted-foreground">
        En verifieringskod skickas till ditt registrerade mobilnummer.
      </p>

      {status === 'idle' && (
        <Button onClick={sendCode} className="w-full">
          <Smartphone className="h-4 w-4 mr-2" />
          Skicka SMS-kod
        </Button>
      )}

      {status === 'sending' && (
        <div className="flex items-center justify-center gap-2 py-4">
          <Loader2 className="h-5 w-5 animate-spin" />
          <span>Skickar SMS...</span>
        </div>
      )}

      {(status === 'code_sent' || status === 'verifying') && (
        <div className="space-y-3">
          <p className="text-sm">Ange den 6-siffriga koden som skickades via SMS:</p>
          <input
            type="text"
            inputMode="numeric"
            maxLength={6}
            value={code}
            onChange={(e) => setCode(e.target.value.replace(/\D/g, ''))}
            className="w-full text-center text-2xl tracking-widest p-3 border rounded-lg font-mono"
            placeholder="000000"
            autoFocus
          />
          <Button
            onClick={verifyCode}
            disabled={code.length !== 6 || status === 'verifying'}
            className="w-full"
          >
            {status === 'verifying' ? (
              <Loader2 className="h-4 w-4 animate-spin mr-2" />
            ) : null}
            Verifiera
          </Button>
          <Button variant="ghost" size="sm" onClick={sendCode} className="w-full">
            Skicka ny kod
          </Button>
        </div>
      )}

      {status === 'failed' && (
        <div className="space-y-3">
          <p className="text-sm text-destructive">{error}</p>
          <Button onClick={sendCode} variant="outline" className="w-full">
            Försök igen
          </Button>
        </div>
      )}

      {error && status === 'code_sent' && (
        <p className="text-sm text-destructive">{error}</p>
      )}

      <Button variant="ghost" onClick={onCancel} className="w-full">
        Avbryt
      </Button>
    </div>
  );
}
